<template>
  <div 
    class="mb-6"
    :class="containerClasses"
  >
    <!-- Header with counter and optional title -->
    <div 
      v-if="showHeader"
      class="flex justify-between mb-2"
    >
      <span 
        v-if="title"
        :class="titleClasses"
      >
        {{ title }}
      </span>
      <span v-else></span>
      
      <span 
        v-if="showCounter"
        :class="counterClasses"
      >
        {{ current }}/{{ total }}
      </span>
    </div>

    <!-- Progress bar -->
    <div 
      class="w-full rounded-full"
      :class="trackClasses"
      :style="trackStyle"
    >
      <div 
        class="rounded-full transition-all duration-300"
        :class="progressClasses"
        :style="progressStyle"
      >
        <!-- Optional inner text -->
        <span 
          v-if="showInnerText && progressPercentage > 15"
          class="flex items-center justify-center h-full text-xs font-medium text-white px-2"
        >
          {{ innerText || `${Math.round(progressPercentage)}%` }}
        </span>
      </div>
    </div>

    <!-- Optional description -->
    <div 
      v-if="description"
      class="mt-2 text-sm text-gray-600"
    >
      {{ description }}
    </div>

    <!-- Slot for custom content -->
    <slot name="custom-content"></slot>
  </div>
</template>

<script setup>
import { computed } from 'vue';

// Props
const props = defineProps({
  // Main data
  current: {
    type: Number,
    required: true,
    validator: (value) => value >= 0
  },
  total: {
    type: Number,
    required: true,
    validator: (value) => value > 0
  },
  
  // Display options
  showHeader: {
    type: Boolean,
    default: true
  },
  showCounter: {
    type: Boolean,
    default: true
  },
  showInnerText: {
    type: Boolean,
    default: false
  },
  
  // Content customization
  title: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  innerText: {
    type: String,
    default: ''
  },
  
  // Styling
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'success', 'warning', 'danger', 'info'].includes(value)
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  
  // Custom dimensions
  width: {
    type: [String, Number],
    default: null
  },
  maxWidth: {
    type: [String, Number],
    default: 380
  },
  
  // Custom colors
  progressColor: {
    type: String,
    default: ''
  },
  trackColor: {
    type: String,
    default: ''
  },
  textColor: {
    type: String,
    default: ''
  },
  
  // Advanced options
  animated: {
    type: Boolean,
    default: true
  },
  striped: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['progress-change', 'complete']);

// Computed properties
const progressPercentage = computed(() => {
  if (props.total === 0) return 0;
  const percentage = Math.min((props.current / props.total) * 100, 100);
  return Math.max(percentage, 0);
});

// Size configurations
const sizeConfig = computed(() => {
  const configs = {
    small: {
      height: 1.5,
      containerClass: 'text-xs',
      titleClass: 'text-xs font-medium',
      counterClass: 'text-xs font-medium'
    },
    medium: {
      height: 2.5,
      containerClass: 'text-sm',
      titleClass: 'text-sm font-medium',
      counterClass: 'text-sm font-medium'
    },
    large: {
      height: 3.5,
      containerClass: 'text-base',
      titleClass: 'text-base font-semibold',
      counterClass: 'text-base font-semibold'
    }
  };
  return configs[props.size];
});

// Color variant configurations
const variantConfig = computed(() => {
  const configs = {
    default: {
      progress: 'bg-tuin-100',
      track: 'bg-[#D9D9D9]',
      text: 'text-custom_gray'
    },
    success: {
      progress: 'bg-green-500',
      track: 'bg-green-100',
      text: 'text-green-700'
    },
    warning: {
      progress: 'bg-yellow-500',
      track: 'bg-yellow-100',
      text: 'text-yellow-700'
    },
    danger: {
      progress: 'bg-red-500',
      track: 'bg-red-100',
      text: 'text-red-700'
    },
    info: {
      progress: 'bg-blue-500',
      track: 'bg-blue-100',
      text: 'text-blue-700'
    }
  };
  return configs[props.variant];
});

// Computed classes and styles
const containerClasses = computed(() => {
  const classes = [sizeConfig.value.containerClass];
  
  if (props.width) {
    classes.push('w-full');
  } else {
    classes.push('w-full', 'sm:w-[380px]');
  }
  
  return classes;
});

const titleClasses = computed(() => [
  sizeConfig.value.titleClass,
  props.textColor || variantConfig.value.text
]);

const counterClasses = computed(() => [
  sizeConfig.value.counterClass,
  props.textColor || variantConfig.value.text
]);

const trackClasses = computed(() => [
  props.trackColor || variantConfig.value.track
]);

const trackStyle = computed(() => {
  const style = {
    height: `${sizeConfig.value.height * 4}px` // Convert rem to px
  };
  
  if (props.width) {
    style.width = typeof props.width === 'number' ? `${props.width}px` : props.width;
    if (props.maxWidth) {
      style.maxWidth = typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth;
    }
  }
  
  return style;
});

const progressClasses = computed(() => {
  const classes = [
    props.progressColor || variantConfig.value.progress,
    'h-full'
  ];
  
  if (props.striped) {
    classes.push('progress-striped');
  }
  
  if (props.animated) {
    classes.push('transition-all', 'duration-300');
  }
  
  return classes;
});

const progressStyle = computed(() => {
  const style = {
    width: `${progressPercentage.value}%`
  };
  
  return style;
});

// Watch for changes and emit events
import { watch } from 'vue';

watch(progressPercentage, (newVal, oldVal) => {
  emit('progress-change', {
    percentage: newVal,
    current: props.current,
    total: props.total,
    previousPercentage: oldVal
  });
  
  if (newVal === 100 && oldVal < 100) {
    emit('complete', {
      current: props.current,
      total: props.total
    });
  }
});
</script>

<style scoped>
/* Custom text color for consistency with existing design */
.text-custom_gray {
  color: #6B7280;
}

/* Striped progress bar effect */
.progress-striped {
  background-image: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.15) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.15) 50%,
    rgba(255, 255, 255, 0.15) 75%,
    transparent 75%,
    transparent
  );
  background-size: 1rem 1rem;
  animation: progress-bar-stripes 1s linear infinite;
}

@keyframes progress-bar-stripes {
  0% {
    background-position: 1rem 0;
  }
  100% {
    background-position: 0 0;
  }
}
</style>